export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

export function formatShortDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";

  const now = new Date();
  const includeYear = d.getFullYear() !== now.getFullYear();

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    ...(includeYear ? { year: "numeric" } : {}),
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

export function formatShortDateTimeWithZone(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";

  const now = new Date();
  const includeYear = d.getFullYear() !== now.getFullYear();

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    ...(includeYear ? { year: "numeric" } : {}),
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(d);
}

export function formatShortDateTimeInTimeZone(
  iso: string | null,
  timeZoneName: string | null | undefined,
): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";

  const resolvedTimeZone =
    typeof timeZoneName === "string" && timeZoneName.trim().length > 0
      ? timeZoneName.trim()
      : null;
  if (!resolvedTimeZone) {
    return formatShortDateTimeWithZone(iso);
  }

  const now = new Date();
  const includeYear = d.getFullYear() !== now.getFullYear();

  try {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      ...(includeYear ? { year: "numeric" } : {}),
      hour: "numeric",
      minute: "2-digit",
      timeZone: resolvedTimeZone,
      timeZoneName: "short",
    }).format(d);
  } catch {
    return formatShortDateTimeWithZone(iso);
  }
}

export function formatDuration(
  started_at: string,
  ended_at: string | null
): string {
  const start = new Date(started_at);
  const end = ended_at ? new Date(ended_at) : new Date();
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "—";
  const ms = end.getTime() - start.getTime();
  const minutes = Math.floor(ms / 1000 / 60);
  if (minutes < 1) return "< 1 min";
  return `${minutes} min`;
}

export function formatBytes(bytes: number | null | undefined): string {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  const digits = size >= 100 || unitIndex === 0 ? 0 : size >= 10 ? 1 : 2;
  return `${size.toFixed(digits)} ${units[unitIndex]}`;
}

export function formatUserIdBadge(userId: string | null | undefined): string | null {
  if (!userId) return null;

  const normalized = userId.replace(/[^a-zA-Z0-9]/g, "");
  const shortToken = (normalized || userId).slice(0, 8);
  if (!shortToken) return null;

  return `U-${shortToken}`;
}

function maskIpv4Address(value: string): string {
  const [firstOctet, secondOctet] = value.split(".");
  if (!firstOctet || !secondOctet) return "***.***.***.***";
  return `${firstOctet}.${secondOctet}.xxx.xxx`;
}

function maskIpv6Address(value: string): string {
  const [head] = value.split("::");
  const visibleSegments = head
    .split(":")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .slice(0, 2);

  if (value.includes("::")) {
    return visibleSegments.length > 0
      ? `${visibleSegments.join(":")}::****`
      : "::****";
  }

  if (visibleSegments.length > 0) {
    return `${visibleSegments.join(":")}:****:****`;
  }

  return "****:****";
}

export function formatMaskedIpAddress(
  value: string | null | undefined,
): string | null {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  const ipv4MappedMatch = trimmed.match(
    /^::ffff:(\d{1,3}(?:\.\d{1,3}){3})$/i,
  );
  if (ipv4MappedMatch) {
    return `::ffff:${maskIpv4Address(ipv4MappedMatch[1])}`;
  }

  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(trimmed)) {
    return maskIpv4Address(trimmed);
  }

  if (trimmed.includes(":")) {
    return maskIpv6Address(trimmed);
  }

  return trimmed.length <= 4
    ? "****"
    : `${trimmed.slice(0, 2)}***${trimmed.slice(-2)}`;
}

export function msToTimeLabel(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
